import { useRouter } from 'next/router'
import { Trans, t } from '@lingui/macro'
import imageUrlBuilder from '@sanity/image-url'
import client from '@lib/sanity'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import { FaCommentAlt } from 'react-icons/fa'
import H1 from 'components/ui/H1'
import Header from 'components/ui/Header'
import Content from 'components/Content'
import { FaGlobe } from 'react-icons/fa'
import { locales, localeLabels } from '@lib/i18n'
import ButtonLink from 'components/ButtonLink'
import SidebarHeading from 'components/ui/SidebarHeading'
import H2 from 'components/ui/H2'
import Index from 'components/Index'

const TeamMemberView = ({ teamMember, posts }) => {
  const { locale } = useRouter()

  const translations = () => {
    const out = []
    for (const loc in locales) {
      if (teamMember.bio[loc] && loc !== locale) {
        out.push(
          <li key={`translation-${loc}`}>
            <Link
              href="/team/[slug]"
              as={`/team/${teamMember.slug.current}`}
              locale={loc}
            >
              <a className="block">
                <Trans id={localeLabels[loc].id} />
              </a>
            </Link>
          </li>
        )
      }
    }
    return <ul className="grid grid-cols-2 md:grid-cols-1">{out}</ul>
  }

  return (
    <div className="pb-10">
      <Head>
        <title key="title">
          {`${teamMember.name} - ${process.env.NEXT_PUBLIC_SITE_TITLE}`}
        </title>
        <meta property="og:title" content={teamMember.name} key="og:title" />
        <meta property="og:type" content="profile" key="og:type" />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/team/${teamMember.slug.current}`}
          key="canonical"
        />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_DOMAIN}${
            locale == 'en' ? `` : `/${locale}`
          }/team/${teamMember.slug.current}`}
          key="og:url"
        />
        {teamMember.mainImage && teamMember.mainImage.asset && (
          <meta
            property="og:image"
            content={imageUrlBuilder(client)
              .image(teamMember.mainImage)
              .width(600)
              .url()}
            key="og:image"
          />
        )}
      </Head>
      <article>
        <div className="lg:hidden">
          {teamMember.mainImage && (
            <div className="relative">
              <div className="relative aspect-video w-full bg-primary-100 md:aspect-[3/1]">
                <Image
                  src={imageUrlBuilder(client)
                    .image(teamMember.mainImage)
                    .width(600)
                    .url()}
                  alt={teamMember.name}
                  layout="fill"
                  objectFit="contain"
                  objectPosition="center top"
                  priority={true}
                />
              </div>
            </div>
          )}
        </div>
        <div className="container">
          <Header>
            <p className="mb-4 text-center font-medium uppercase 2xl:mb-10">
              <Link href="/team">
                <a>
                  {teamMember.teamGroup && teamMember.teamGroup.length
                    ? teamMember.teamGroup[0].name[locale]
                    : t`Profile`}
                </a>
              </Link>
            </p>
            <H1>{teamMember.name}</H1>
            <h2 className="mt-4 text-center text-base uppercase md:text-xl">
              {teamMember.role[locale]}
            </h2>
          </Header>
          <div className="flex flex-col md:flex-row">
            <div className="mb-10 md:mr-10 md:basis-2/3">
              <Content content={teamMember.bio[locale]} />
              {teamMember.contact && (
                <div className="my-4 flex md:my-10">
                  <ButtonLink
                    href={`mailto:${teamMember.contact}`}
                    variant="secondary"
                  >
                    <FaCommentAlt />
                    <span className="ml-2">
                      <Trans>Contact</Trans>
                    </span>
                  </ButtonLink>
                </div>
              )}
            </div>
            <div className="md:basis-1/3">
              <div className="hidden lg:block">
                {teamMember.mainImage && (
                  <div className="relative mx-auto mb-10 aspect-square w-32 border border-primary-200 bg-idpcBackground md:w-64 lg:mx-0 lg:w-full">
                    <Image
                      src={imageUrlBuilder(client)
                        .image(teamMember.mainImage)
                        .width(360)
                        .url()}
                      alt={teamMember.name}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      priority={true}
                    />
                  </div>
                )}
              </div>
              <div className="mb-10">
                <SidebarHeading>
                  <FaGlobe className="mr-2" />
                  <Trans>Translations</Trans>
                </SidebarHeading>
                <div className="mb-4">{translations()}</div>
              </div>
            </div>
          </div>
        </div>
      </article>
      {posts && !!posts.length && (
        <div className="py-10">
          <div className="container">
            <H2>
              <Trans>Related Content</Trans>
            </H2>
            <Index posts={posts} />
          </div>
        </div>
      )}
    </div>
  )
}
export default TeamMemberView
